import { cn } from '@/shared/lib/utils'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/shared/components/ui/table'
import type {
  BankStatementAnalysis, BankStatementParser, ManualUploadAccount,
} from '../types/bank-statement'

interface StatementAnalysisListProps {
  analyses: BankStatementAnalysis[]
  parsers: BankStatementParser[]
  accounts: ManualUploadAccount[]
  accountIdLock?: number
  onParserChange: (index: number, parserId: number) => void
  onAccountChange: (index: number, accountId: number) => void
}

function formatBalance(value: number | null | undefined): string {
  return value !== null && value !== undefined ? String(value) : '---'
}

function accountLabel(a: ManualUploadAccount): string {
  if (a.displayName) return a.displayName
  return [a.accountName, a.companyName].filter(Boolean).join(' — ')
}

export function StatementAnalysisList({
  analyses, parsers, accounts, accountIdLock,
  onParserChange, onAccountChange,
}: StatementAnalysisListProps) {
  const showNoAccountWarning = !accountIdLock && analyses.some((a) => !a.accountId)
  const inErrorState = analyses.some((a) => a.inErrorState)
  const allErrors = analyses.flatMap((a) => a.errors ?? [])

  return (
    <div className="border rounded-md">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>File name</TableHead>
            <TableHead>Parser</TableHead>
            <TableHead>Account</TableHead>
            <TableHead className="text-right">Opening</TableHead>
            <TableHead className="text-right">Closing</TableHead>
            <TableHead className="text-center">Currency</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {analyses.map((analysis, i) => (
            <TableRow key={`${analysis.fileName}-${i}`}>
              <TableCell className={cn(analysis.inErrorState && 'text-destructive')}>
                {analysis.fileName}
                {!analysis.accountId && !accountIdLock && <span> *</span>}
              </TableCell>
              <TableCell>
                {analysis.inErrorState ? '—' : (
                  <select
                    className="border rounded-sm px-2 py-1 text-sm bg-background"
                    value={analysis.parser.id || ''}
                    onChange={(e) => onParserChange(i, Number(e.target.value))}
                  >
                    <option value="">Select parser</option>
                    {parsers.map((p) => (
                      <option key={p.Id} value={p.Id}>{p.Name}</option>
                    ))}
                  </select>
                )}
              </TableCell>
              <TableCell>
                {analysis.inErrorState ? '—' : (
                  <select
                    className="border rounded-sm px-2 py-1 text-sm bg-background disabled:opacity-60"
                    value={analysis.accountId || ''}
                    disabled={accountIdLock !== undefined}
                    onChange={(e) => onAccountChange(i, Number(e.target.value))}
                  >
                    <option value="">Select account</option>
                    {accounts.map((a) => (
                      <option key={a.id} value={a.id}>{accountLabel(a)}</option>
                    ))}
                  </select>
                )}
              </TableCell>
              <TableCell className="text-right font-mono">
                {formatBalance(analysis.bankStatementSummary?.openingBalance?.value)}
              </TableCell>
              <TableCell className="text-right font-mono">
                {formatBalance(analysis.bankStatementSummary?.closingBalance?.value)}
              </TableCell>
              <TableCell className="text-center">
                {analysis.bankStatementSummary?.currency ?? '---'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {(showNoAccountWarning || inErrorState) && (
        <div className="px-3 py-2 text-xs border-t space-y-1">
          {showNoAccountWarning && (
            <p className="text-amber-600">
              * Could not match an account automatically — please select one.
            </p>
          )}
          {inErrorState && (
            <div className="text-destructive">
              <p>One or more files could not be processed:</p>
              {allErrors.map((err, i) => (
                <p key={i} className="text-[11px]">{err}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
